import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import type { AppDispatch } from '@redux/store'

import {
  generateApplicationUrl,
  getQueryParamsFromHash,
  launchModal,
  parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import * as trainingCourseActions from '@redux/modules/trainingCourse'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultFormState, { requiredFields } from '@models/trainingCourse'
import type { TrainingCourseModel, TrainingCourseRequestOptions } from '@models/types'

export const generateUrls = (trainingCourse: Partial<TrainingCourseModel> = {}) => {
  const { id } = trainingCourse

  // @ts-expect-error TS2339
  const { applicationUrl } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  return {
    editTrainingCourseUrl: `#/systemManager/courses/${id}/Edit`,
    linkUrl: `${applicationUrl}/training/dashboard#/courses/${id}`,
    oldTrainingDashboardIndex: '/training/dashboard#/',
    trainingCoursesIndexUrl: '#/systemManager/courses',
  }
}

type CreateTrainingCourseParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingCourseRequestOptions,
  trainingCourseParams: Partial<TrainingCourseModel>,
}

const createTrainingCourse = (params: CreateTrainingCourseParams) => {
  const {
    trainingCourseParams, dispatch, requestOptions,
  } = params

  const { createTrainingCourse: createFn } = trainingCourseActions

  return dispatch(createFn(trainingCourseParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTrainingCourseUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTrainingCourseUrl,
        success,
      }
    }

    return response
  })
}

type DuplicateTrainingCourseParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingCourseRequestOptions,
  trainingCourse: TrainingCourseModel,
  trainingCourseParams: Partial<TrainingCourseModel>,
}

const duplicateTrainingCourse = (params: DuplicateTrainingCourseParams) => {
  const {
    trainingCourse, trainingCourseParams, dispatch, requestOptions,
  } = params

  const { duplicateTrainingCourse: duplicateFn } = trainingCourseActions

  const updatedParams = {
    id: trainingCourse.id,
    ...trainingCourseParams,
  }

  // @ts-expect-error TS2345
  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTrainingCourseUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTrainingCourseUrl,
        success,
      }
    }

    return response
  })
}

type UpdateTrainingCourseParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingCourseRequestOptions,
  trainingCourse: TrainingCourseModel,
  trainingCourseParams: Partial<TrainingCourseModel>,
}

const updateTrainingCourse = (params: UpdateTrainingCourseParams) => {
  const {
    trainingCourse, trainingCourseParams, dispatch, requestOptions,
  } = params

  const { updateTrainingCourse: updateFn } = trainingCourseActions

  const updatedParams = {
    id: trainingCourse.id,
    ...trainingCourseParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTrainingCoursePayload = {
  dispatch: AppDispatch,
  trainingCourse: TrainingCourseModel,
}

const deleteTrainingCourse = (params: DeleteTrainingCoursePayload) => {
  const { trainingCourse, dispatch } = params
  const { deleteTrainingCourse: deleteFn } = trainingCourseActions

  return dispatch(deleteFn(trainingCourse)).then((response) => {
    const { success } = response

    if (success){
      const { trainingCoursesIndexUrl } = generateUrls()
      const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

      return {
        ...response,
        redirectUrl: redirect || trainingCoursesIndexUrl,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTrainingCourseForm(
  trainingCourse: Partial<TrainingCourseModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const trainingCourseForm = useForm(
    defaultFormState,
    { entity: trainingCourse, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [trainingCourse.id, trainingCourse.cache_key],
  )

  return {
    ...trainingCourseForm,
  }
}

function useTrainingCourse(initEntity: Partial<TrainingCourseModel> = {}) {
  const { entity: trainingCourse } = useLatestEntity(initEntity, 'trainingCourses')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.trainingCourses)

  // @ts-expect-error TS2339
  const isLoaded = loadedIds.includes(trainingCourse.id)

  return {
    callbacks: {
      createOrDuplicateTrainingCourse: () => launchModal({
        callbacks,
        modalKey: 'CreateOrDuplicateTrainingCourseModal',
        payload: { trainingCourse },
      }),
      createTrainingCourse: (
        trainingCourseParams: Partial<TrainingCourseModel>,
        requestOptions?: TrainingCourseRequestOptions,
      ) => createTrainingCourse({ trainingCourseParams, dispatch, requestOptions }),
      // @ts-expect-error TS2322
      deleteTrainingCourse: () => deleteTrainingCourse({ trainingCourse, dispatch }),
      duplicateTrainingCourse: (
        trainingCourseParams: Partial<TrainingCourseModel>,
        requestOptions?: TrainingCourseRequestOptions,
      ) => duplicateTrainingCourse({
        // @ts-expect-error TS2322
        trainingCourse, trainingCourseParams, dispatch, requestOptions,
      }),
      updateTrainingCourse: (
        trainingCourseParams: Partial<TrainingCourseModel>,
        requestOptions?: TrainingCourseRequestOptions,
      ) => updateTrainingCourse({
        // @ts-expect-error TS2322
        trainingCourse, trainingCourseParams, dispatch, requestOptions,
      }),
    },
    creating,
    isLoaded,
    loading: loading && !isLoaded,
    trainingCourse,
    updating,
    urls: generateUrls(trainingCourse),
  }
}

export default useTrainingCourse
